import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import {
  Button,
  Tooltip,
  Avatar,
  Layout,
  Typography,
  Row,
  Col,
  Drawer,
  Grid,
} from 'antd';
import Sider from 'antd/es/layout/Sider';
import { Link, NavLink, Outlet, useLocation, useNavigate } from 'react-router-dom';
import {
  AlarmClockIcon,
  ArrowReloadHorizontalIcon,
  Building03Icon,
  Analytics02Icon,
  Calendar03Icon,
  CalendarCheckOut02Icon,
  FileStarIcon,
  MoneyExchange02Icon,
  MoneySend01Icon,
  StarIcon,
  MessageMultiple01Icon,
  TapeMeasureIcon,
} from 'hugeicons-react';

import {
  BellOutlined,
  MenuUnfoldOutlined,
  BarChartOutlined,
  MessageOutlined,
  PaperClipOutlined,
  FileAddOutlined,
} from '@ant-design/icons';
import Logo from './../assets/Logo.png';
import DocIcon from './../assets/icons/doc.svg';
import DocIconWhite from './../assets/icons/doc-white.svg';
import HomeIcon from './../assets/icons/home.svg';
import HomeIconWhite from './../assets/icons/home-white.svg';
import ProfileIcon from './../assets/icons/profile.svg';
import ProfileIconWhite from './../assets/icons/profile-white.svg';
import ExitIcon from './../assets/icons/exit.svg';
import ExitIconWhite from './../assets/icons/exit-white.svg';
import PaperIcon from './../assets/icons/paper.svg';
import NotifyIcon from './../assets/icons/notify.svg';
import ValletIcon from './../assets/icons/vallet.svg';
import ValletIconWhite from './../assets/icons/vallet-white.svg';
import SettingsIcon from './../assets/icons/settings.svg';
import SettingsIconWhite from './../assets/icons/setting-white.svg';
import ConfirmIcon from './../assets/confirm.png';
import ConfirmIconGreen from './../assets/confirm-green.png';
import DocConfirm from './../assets/doc-confirm.svg';
import DocConfirmWhite from './../assets/doc-confirm-white.svg';
import PaperIconWhite from './../assets/icons/paper-white.svg';
import './layouts.scss';
import ImageService from '../services/ImageService';
import axios from 'axios';
import Notifications from '../components/Notifications/Notifications';
import WeeklyPlanDrawer from '../views/Planning/components/WeeklyPlanDrawer';
import { clearStorage, hasRole, redirectToLoginWithHardReload } from '../utils';
import { getInspector, listInspectorsAll } from '../modules/inspectors/services/inspectorService';
import { getMeasuringInstrumentInventory } from '../modules/measuring-instrument-inventories/services/measuring-instrument-inventoryService';
import { ChartLineData02Icon } from 'hugeicons-react';
import { ChartBarLineIcon } from 'hugeicons-react';
import { AnalyticsUpIcon } from 'hugeicons-react';
import { CheckListIcon } from 'hugeicons-react';
import { Home02Icon } from 'hugeicons-react';
import { Agreement02Icon } from 'hugeicons-react';
import { Home03Icon } from 'hugeicons-react';
import { Home04Icon } from 'hugeicons-react';
import { UserMultiple02Icon } from 'hugeicons-react';
import { WalletDone02Icon } from 'hugeicons-react';
import FinanceSidebar from './FinanceSidebar';
import dayjs, { Dayjs } from 'dayjs';
import isoWeek from 'dayjs/plugin/isoWeek';
import packageJson from '../../package.json';
import { TaskDaily01Icon } from 'hugeicons-react';

dayjs.extend(isoWeek);

const appVersion = (process.env.REACT_APP_VERSION || packageJson.version || '1.0.0').trim();
const VERSION_STORAGE_KEY = 'normcert-web-client-version';

const compareSemver = (first: string, second: string) => {
  const parseSegment = (segment: string) => Number.parseInt(segment, 10) || 0;
  const firstParts = first.split('.').map(parseSegment);
  const secondParts = second.split('.').map(parseSegment);
  const maxLength = Math.max(firstParts.length, secondParts.length);

  for (let i = 0; i < maxLength; i += 1) {
    const firstValue = firstParts[i] ?? 0;
    const secondValue = secondParts[i] ?? 0;
    if (firstValue > secondValue) return 1;
    if (firstValue < secondValue) return -1;
  }

  return 0;
};

const { Header, Content } = Layout;
const { Title, Text } = Typography;
const { useBreakpoint } = Grid;

  const MainLayout = () => {
  useEffect(() => {
    const storedVersion = localStorage.getItem(VERSION_STORAGE_KEY);

    if (storedVersion && compareSemver(storedVersion, appVersion) > 0) {
      localStorage.setItem(VERSION_STORAGE_KEY, appVersion);
      window.location.reload();
      return;
    }

    localStorage.setItem(VERSION_STORAGE_KEY, appVersion);
  }, []);

    const navigate = useNavigate();
  const location = useLocation();
  const screens = useBreakpoint();
  const isMobile = !screens.md;

  const [loaded, setLoaded] = useState(false);
  const [isCustomer, setIsCustomer] = useState(false);

  const [firstName, setFirstName] = useState('');
  const [lastName, setLastName] = useState('');
  const [profilePhoto, setProfilePhoto] = useState('');
  const [imageId, setImageId] = useState<string | null>(() => localStorage.getItem('profilePhoto'));
  const [imageRefreshToken, setImageRefreshToken] = useState(0);
  const [roles, setRoles] = useState<any[]>([]);
  const [profileCompletion, setProfileCompletion] = useState(localStorage.getItem('isPartner'));

  // permissions (array) state:
  const [permissions, setPermissions] = useState<string[]>([]);
  const isInspector = roles?.some((r: any) => r?.name === 'INSPECTOR');
  const [inspectorFullName, setInspectorFullName] = useState<string | null>(null);
  const [miInventoryTitle, setMiInventoryTitle] = useState<string | null>(null);
  const isFinance = useMemo(
    () => roles?.some((r: any) => r?.name === 'FINANCE') || hasRole('FINANCE'),
    [roles],
  );
  const isAdmin = useMemo(
    () => roles?.some((r: any) => r?.name === 'ADMIN') || hasRole('ADMIN'),
    [roles],
  );
  const isCentralUser = useMemo(
    () => roles?.some((r: any) => r?.name === 'CENTRAL_USER') || hasRole('CENTRAL_USER'),
    [roles],
  );
  const canSeeWeeklyPlan = isAdmin || isCentralUser;
  const [weeklyPlanDrawerOpen, setWeeklyPlanDrawerOpen] = useState(false);
  const [weeklyPlanWeekStart, setWeeklyPlanWeekStart] = useState<Dayjs>(() => dayjs().startOf('isoWeek'));
  const [weeklyInspectors, setWeeklyInspectors] = useState<any[]>([]);
  const [weeklyInspectorsLoaded, setWeeklyInspectorsLoaded] = useState(false);

  // Drawer (mobil menü) state
  const [drawerVisible, setDrawerVisible] = useState(false);

  const previousImageDataRef = useRef<{ id: string | null; token: number }>({
    id: imageId,
    token: 0,
  });

  const triggerProfilePhotoReload = useCallback((newValue?: string | null) => {
    setImageRefreshToken((prev) => prev + 1);
    setProfilePhoto('');
    if (typeof newValue === 'undefined') {
      setImageId(localStorage.getItem('profilePhoto'));
    } else {
      setImageId(newValue);
    }
  }, []);

  // Dinamik başlık için routeTitles
  const routeTitles: Record<string, string> = {
    '/': 'Genel Görünüm',
    '/inspector': 'Genel Görünüm',
    '/inspector/plan': 'Görevlerim',
    '/inspector/devices': 'Cihazlarım',
    '/inspector/professions': 'Mesleklerim',
    '/inspector/external-documents': 'Dış Kaynaklı Dokümanlar',
    '/inspector/nonconformity-reasons': 'Uygunsuzluk Nedenleri',
    '/profile': 'Hesap Bilgileri',
    '/companies': 'Firmalar',
    '/proposals': 'Teklif Yönetimi',
    '/mailing': 'Mailing Yönetimi',
    '/mailing/contacts': 'Mailing Yönetimi',
    '/mailing/templates': 'Mailing Yönetimi',
    '/mailing/campaigns': 'Mailing Yönetimi',
    '/mailing/surveys': 'Mailing Yönetimi',
    '/users': 'Kullanıcılar',
    '/wallet': 'Cüzdan',
    '/roles': 'Ayarlar',
    '/permissions': 'Yetki Yönetimi',
    '/notes': 'Notlar',
    '/tickets': 'Destek',
    '/all-tickets': 'Destek',
    '/statuses': 'Durumlar',
    '/settings': 'Ayarlar',
    '/measuring-instrument-inventories': 'Cihaz Envanteri',
    '/settings/statuses': 'Statüler',
    '/settings/discount-rates': 'İndirim Oranları',
    '/settings/tools': 'Bileşenler',
    '/settings/parameters': 'Parametre Yönetimi',
    '/settings/parameters/reports': 'Rapor Format Durumu',
    '/settings/control-areas': 'Parametre Tip-Özellik',
    '/settings/offerings': 'Hizmet Grubu Yönetimi',
    '/settings/groups': 'Kategori Yönetimi',
    '/settings/system-settings': 'Sistem Ayarları',
    '/settings/roles': 'Roller',
    '/settings/sms-tests': 'SMS Test',
    '/settings/agreements': 'Sözleşmeler',
    '/settings/announcements': 'Duyurular',
    '/settings/organization': 'Organizasyon Şeması',
    '/settings/organizastion': 'Organizasyon Şeması',
    '/settings/states': 'İller',
    '/settings/districts': 'İlçeler',
    '/settings/access-inventory': 'Erişim Envanteri',
    '/settings/professions': 'Meslekler',
    '/settings/measuring-instruments': 'Ölçüm Cihazları',
    '/settings/measuring-instrument-inventories': 'Cihaz Envanteri',
    '/settings/measuring-instrument-calibrations': 'Cihaz Kalibrasyonları',
    '/settings/measuring-instrument-inspectors': 'Cihaz Denetçileri',
    '/settings/public-holidays': 'Resmi Tatiller',
    '/settings/inspectors': 'P.K.M Uzmanları',
    '/settings/inspector-professions': 'Denetçi Meslekleri',
    '/settings/leaves': 'İzinler',
    '/settings/external-documents': 'Dış Kaynaklı Doküman',
    '/settings/report-nodes': 'Rapor Yapısı',
    '/settings/questions': 'Sorular',
    '/settings/answers': 'Cevaplar',
    '/settings/negative-reasons': 'Olumsuz Teklif Nedenleri',
    '/settings/nonconformity-reasons': 'Uygunsuzluk Nedenleri',
    '/settings/inspection-appointments': 'Denetim Randevuları',
    '/settings/inspection-tasks': 'Denetim Görevleri',
    '/project-followup': 'İş Yönetimi',
    '/proposals/create': 'Teklif Oluştur',
    '/proposals/report': 'Rapor Detayları',
    '/all-notifications': 'Duyurular',
    '/reports': 'Raporlar',
    '/opportunity': 'Ajanda',
    '/reminders': 'Görevler',
    '/payments': 'Ödemeler',
  };

  // Sayfa başlığını bulma
  const getPageTitle = (pathname: string) => {
  const companyRegex = /\/companies\/\d+$/;
  const companyNonconformitiesRegex = /\/companies\/\d+\/nonconformities$/;
  const companyControlReportsRegex = /\/companies\/\d+\/control-reports$/;
  const userRegex = /\/users\/\d+$/;
  const miInventoryRegex = /\/measuring-instrument-inventories\/\d+$/;
  const inspectorDetailRegex = /\/inspectors\/\d+$/;
  const planningDetailRegex = /\/planning\/detail\/\d+$/;
  const appointmentDetailRegex = /\/inspection-appointments\/\d+$/;
  const reportFormatDetailRegex = /\/report-formats\/detail\/\d+$/;
  const reportFormatListByAttrRegex = /\/report-formats\/\d+$/;
  const reportNodesViewAllRegex = /\/report-nodes\/view-all\/[^/]+$/;

  // ✅ yeni ekleme
  const inspectorPlanningDetailRegex = /\/inspector\/planning\/detail\/\d+$/;
  const controlTaskRegex = /^\/control\/\d+\/taskId\/\d+\/attribute\/\d+\/order\/\d+$/;
  const controlTaskRegexReport = /^\/control\/\d+\/taskId\/\d+\/attribute\/\d+\/order\/\d+\/report$/;

  // Özel karşılama mesajı
  if (pathname === '/' && roles && roles.some((role: any) => role.name === 'CUSTOMER')) {
    return (
      <span>
        <b style={{ fontWeight: '700' }}>Hoşgeldin {firstName}!</b>{' '}
        <span style={{ color: '#4A576E', fontWeight: 500 }}>
          Gelirini artırmak için <br /> hemen teklif oluştur ve fırsatları değerlendir!
        </span>
      </span>
    );
  }

  if (companyControlReportsRegex.test(pathname)) {
    return 'Firma Kontrol Raporları';
  } else if (companyNonconformitiesRegex.test(pathname)) {
    return 'Firma Uygunsuzlukları';
  } else if (companyRegex.test(pathname)) {
    return 'Firma Detayı';
  } else if (userRegex.test(pathname)) {
    return 'Kullanıcı Detayı';
  } else if (miInventoryRegex.test(pathname)) {
    return miInventoryTitle || 'Cihaz Envanteri Detayı';
  } else if (inspectorDetailRegex.test(pathname)) {
    return 'P.K.M Uzman Detayı';
  } else if (appointmentDetailRegex.test(pathname)) {
    return 'Denetim Randevusu Detayı';
  } else if (reportFormatDetailRegex.test(pathname)) {
    return 'Rapor Formatı Detayı';
  } else if (reportFormatListByAttrRegex.test(pathname)) {
    return 'Rapor Formatı Listesi';
  } else if (reportNodesViewAllRegex.test(pathname)) {
    return 'Rapor Önizle';
  } else if (inspectorPlanningDetailRegex.test(pathname)) {
    // ✅ yeni başlık
    return 'Periyodik Kontrol Planı';
  } else if (controlTaskRegex.test(pathname)) {
    return 'Periyodik Kontrol Formu';
  } else if ( controlTaskRegexReport.test(pathname)) {
    return 'Periyodik Kontrol Raporu';
  } else if (planningDetailRegex.test(pathname)) {
    return 'Planla';
  }  else {
    return routeTitles[pathname as keyof typeof routeTitles] || 'Sayfa Bulunamadı';
  }
};

  const pageTitle = getPageTitle(location.pathname);
  const renderPageTitle = () => {
    const hasHtmlTags = typeof pageTitle === 'string' && /<\/?[a-z][\s\S]*>/i.test(pageTitle);
    if (hasHtmlTags) {
      return <span dangerouslySetInnerHTML={{ __html: pageTitle }} />;
    }
    return pageTitle;
  };

  useEffect(() => {
    const accessToken = localStorage.getItem('accessToken');
    const storedFirstName = localStorage.getItem('firstName');
    const storedLastName = localStorage.getItem('lastName');
    const storedRoles = localStorage.getItem('roles');
    const parsedRoles = storedRoles ? JSON.parse(storedRoles) : [];
    setRoles(parsedRoles);

    setIsCustomer(hasRole('CUSTOMER'));


    // Permissions'ı localStorage'dan çekme
    const storedPermissions = localStorage.getItem('permissions');
    if (storedPermissions) {
      try {
        const parsedPermissions = JSON.parse(storedPermissions);
        if (Array.isArray(parsedPermissions)) {
          setPermissions(parsedPermissions);
        } else {
          console.error('Permissions hatalı formatta:', parsedPermissions);
        }
      } catch (err) {
        console.error('Permissions parse hatası:', err);
      }
    }

    const pCompletion = localStorage.getItem('isPartner');
    setFirstName(storedFirstName ?? '');
    setLastName(storedLastName ?? '');
    setProfileCompletion(pCompletion);
    if (!accessToken || accessToken === 'null') {
      logout();
    } else {
      setLoaded(true);
    }
  }, [location.pathname]);

  useEffect(() => {
    if (!canSeeWeeklyPlan) {
      setWeeklyPlanDrawerOpen(false);
      setWeeklyInspectors([]);
      setWeeklyInspectorsLoaded(false);
      return;
    }

    if (!weeklyPlanDrawerOpen || weeklyInspectorsLoaded) {
      return;
    }

    let ignore = false;

    listInspectorsAll()
      .then((data: any) => {
        if (ignore) {
          return;
        }
        setWeeklyInspectors(Array.isArray(data) ? data : []);
        setWeeklyInspectorsLoaded(true);
      })
      .catch((error) => {
        if (ignore) {
          return;
        }
        console.error('Haftalık plan için denetçiler yüklenemedi:', error);
        setWeeklyInspectors([]);
      });

    return () => {
      ignore = true;
    };
  }, [canSeeWeeklyPlan, weeklyPlanDrawerOpen, weeklyInspectorsLoaded]);

  // Measuring Instrument Inventory: dynamic title
  useEffect(() => {
    const match = location.pathname.match(/\/measuring-instrument-inventories\/(\d+)$/);
    if (match) {
      const id = Number(match[1]);
      if (!isNaN(id)) {
        getMeasuringInstrumentInventory(id)
          .then((d: any) => {
            const name = (d?.measuringInstrument?.name || d?.measuringInstrumentName || d?.name) + ' <small style="color: gray; font-size: 16px; padding-left: 5px">ID:' + id + '</small>';
            setMiInventoryTitle(name ? String(name) : null);
          })
          .catch(() => setMiInventoryTitle(null));
      } else {
        setMiInventoryTitle(null);
      }
    } else {
      setMiInventoryTitle(null);
    }
  }, [location.pathname]);

  // Fetch inspector name for dynamic title when on inspector detail route
  useEffect(() => {
    const match = location.pathname.match(/\/inspectors\/(\d+)$/);
    if (match) {
      const id = Number(match[1]);
      if (!isNaN(id)) {
        getInspector(id)
          .then((d: any) => {
            const first = d?.account?.firstName || '';
            const last = d?.account?.lastName || '';
            const full = `${first} ${last}`.trim();
            setInspectorFullName(full || null);
          })
          .catch(() => setInspectorFullName(null));
      } else {
        setInspectorFullName(null);
      }
    } else {
      setInspectorFullName(null);
    }
  }, [location.pathname]);

  // Inspector: default redirect to inspector dashboard when landing on root
  useEffect(() => {
    if (isInspector && location.pathname === '/') {
      navigate('/inspector');
    }
  }, [isInspector, location.pathname, navigate]);

  // Token yenileme fonksiyonu
  const handleTokenExpiration = async () => {
    console.log('Token süresi dolmuş, yenileniyor...');
    const refreshToken = localStorage.getItem('refreshToken');
    if (!refreshToken) {
      logout();
      return;
    }
    try {
      const apiUrl = process.env.REACT_APP_API_BASE_URL + '/auth/refresh';
      const config = {
        method: 'post',
        maxBodyLength: Infinity,
        url: apiUrl,
        headers: { 'Content-Type': 'application/json' },
        data: JSON.stringify({ refreshToken }),
      };
      axios.request(config).then((response: any) => {
        localStorage.setItem('accessToken', response?.data?.accessToken);
        localStorage.setItem('accessTokenExpiredAt', response?.data?.accessTokenExpiredAt);
        localStorage.setItem('refreshToken', response?.data?.refreshToken);
        localStorage.setItem('refreshTokenExpiredAt', response?.data?.refreshTokenExpiredAt);
      });
    } catch (error) {
      console.error('Token yenileme başarısız:', error);
      logout();
    }
  };

  // Logout fonksiyonu
  const logout = () => {
    clearStorage();
    redirectToLoginWithHardReload();
  };

  // Drawer toggling (mobil menü)
  const toggleDrawer = () => {
    setDrawerVisible(!drawerVisible);
  };

  // Profil resmi fetch
  const fetchImage = useCallback(async (imageIdentifier: string) => {
    try {
      const imageResponse: any = await ImageService.getImage(imageIdentifier);
      const imageUrl = imageResponse?.data?.preSignUrl;

      if (imageUrl) {
        localStorage.setItem('profilePhotoBase64', imageUrl);
        setProfilePhoto(imageUrl);
      }
    } catch (error) {
      console.error('Resim yüklenirken hata oluştu:', error);
    }
  }, []);

  useEffect(() => {
    const handleStorage = (event: StorageEvent) => {
      if (event.key === 'profilePhoto') {
        triggerProfilePhotoReload(event.newValue);
      }
    };

    const handleProfilePhotoUpdated = (event: Event) => {
      const customEvent = event as CustomEvent<string | null>;
      triggerProfilePhotoReload(customEvent.detail);
    };

    window.addEventListener('storage', handleStorage);
    window.addEventListener('profilePhotoUpdated', handleProfilePhotoUpdated as EventListener);

    return () => {
      window.removeEventListener('storage', handleStorage);
      window.removeEventListener('profilePhotoUpdated', handleProfilePhotoUpdated as EventListener);
    };
  }, [triggerProfilePhotoReload]);

  useEffect(() => {
    const previousData = previousImageDataRef.current;
    const hasImageIdChanged = previousData.id !== imageId;
    const hasTokenChanged = previousData.token !== imageRefreshToken;

    previousImageDataRef.current = { id: imageId, token: imageRefreshToken };

    if (!imageId) {
      localStorage.removeItem('profilePhotoBase64');
      setProfilePhoto('');
      return;
    }

    if (hasImageIdChanged || hasTokenChanged) {
      localStorage.removeItem('profilePhotoBase64');
      setProfilePhoto('');
    }

    const currentImageId = imageId;
    const timeoutId = window.setTimeout(() => {
      const cachedImage = localStorage.getItem('profilePhotoBase64');
      if (cachedImage) {
        setProfilePhoto(cachedImage ?? '');
      }

      fetchImage(currentImageId);
    }, 250);

    return () => window.clearTimeout(timeoutId);
  }, [fetchImage, imageId, imageRefreshToken]);

  // Menü öğeleri
  const renderMenuItems = (isMobileMenu = false) => {
    const handleNavigate = () => {
      if (isMobileMenu) {
        setDrawerVisible(false);
      }
    };

    const canSeeReminders =
      roles &&
      Array.isArray(roles) &&
      roles.some((role: any) => ['ADMIN', 'CENTRAL_USER', 'FINANCE', 'INSPECTOR'].includes(role?.name));

    if (isFinance && !isInspector) {
      return (
        <FinanceSidebar
          isMobile={isMobileMenu}
          onNavigate={handleNavigate}
        />
      );
    }

    if (isInspector) {
      const supportLink = permissions.includes('ticket.show') ? '/all-tickets' : '/tickets';
      const inspectorMenuItems = [
        { key: 'dashboard', label: 'Genel Görünüm', to: '/inspector', icon: Home02Icon, tooltip: 'Genel Görünüm', exact: true },
        { key: 'tasks', label: 'Görevlerim', to: '/inspector/plan', icon: Calendar03Icon, tooltip: 'Görevlerim' },
        { key: 'devices', label: 'Cihazlarım', to: '/inspector/devices', icon: TapeMeasureIcon, tooltip: 'Cihazlarım' },
        { key: 'reminders', label: 'Görevler', to: '/reminders', icon: TaskDaily01Icon, tooltip: 'Görevler' },
        { key: 'external-documents', label: 'Dış Kaynaklı Dokümanlar', to: '/inspector/external-documents', icon: FileStarIcon, tooltip: 'Dış Kaynaklı Dokümanlar' },
        { key: 'nonconformity-reasons', label: 'Uygunsuzluk Nedenleri', to: '/inspector/nonconformity-reasons', icon: CheckListIcon, tooltip: 'Uygunsuzluk Nedenleri' },
        { key: 'support', label: 'Destek', to: supportLink, icon: MessageMultiple01Icon, tooltip: 'Destek' },
      ];

      return (
        <div className={`menu-item-container ${isMobileMenu ? 'mobile-menu' : ''}`}>
          {inspectorMenuItems.map(({ key, label, to, icon: Icon, tooltip, exact }) => (
            <NavLink
              key={key}
              to={to}
              end={exact}
              className="menu-link"
              onClick={handleNavigate}
            >
              {({ isActive }) => (
                <div className="menu-item-wrapper">
                  <div className={`menu-item ${isActive ? 'active' : ''}`}>
                    {!isMobileMenu ? (
                      <Tooltip title={tooltip || label} placement="right">
                        <Icon style={{ fontSize: '24px', color: isActive ? 'white' : 'gray' }} />
                      </Tooltip>
                    ) : (
                      <Icon style={{ fontSize: '24px', color: isActive ? 'white' : 'gray' }} />
                    )}
                  </div>
                  {isMobileMenu && <span className="menu-title">{label}</span>}
                </div>
              )}
            </NavLink>
          ))}
        </div>
      );
    }
    return (
    <div className={`menu-item-container ${isMobileMenu ? 'mobile-menu' : ''}`}>
      {/* Ana Sayfa */}
        <NavLink
          to="/"
          className="menu-link"
          onClick={handleNavigate}
        >
        {({ isActive }) => (
          <Tooltip title="Genel Görünüm" placement="right">
            <div className="menu-item-wrapper">
              <div className={`menu-item ${isActive ? 'active' : ''}`}>
                <Home02Icon style={{ fontSize: '24px', color: isActive ? 'white' : 'gray' }} />
              </div>
              {isMobileMenu && <span className="menu-title">Genel Görünüm</span>}
            </div>
          </Tooltip>
        )}
      </NavLink>
      {/* Kullanıcılar */}
      {permissions.includes('users.show') && (
        <NavLink
          to="/users"
          className="menu-link"
          onClick={handleNavigate}
        >
          {({ isActive }) => (
            <div className="menu-item-wrapper">
              <div className={`menu-item ${isActive ? 'active' : ''}`}>
                {!isMobileMenu ? (

                  <Tooltip title="Kullanıcılar" placement="right">
                    <UserMultiple02Icon style={{ fontSize: '24px', color: isActive ? 'white' : 'gray' }} />

                  </Tooltip>
                ) : (
                  <UserMultiple02Icon style={{ fontSize: '24px', color: isActive ? 'white' : 'gray' }} />
                )}
              </div>
              {isMobileMenu && <span className="menu-title">Kullanıcılar</span>}
            </div>
          )}
        </NavLink>
      )}

      {/* Firmalar Building03Icon */}
      <NavLink
        to="/companies"
        className="menu-link"
        onClick={handleNavigate}
      >
        {({ isActive }) => (
          <div className="menu-item-wrapper">
            <div className={`menu-item ${isActive ? 'active' : ''}`}>
              {!isMobileMenu ? (
                <Tooltip title="Firmalar" placement="right">
                  <Building03Icon style={{ fontSize: '24px', color: isActive ? 'white' : 'gray' }} />
                </Tooltip>
              ) : (
                <Building03Icon style={{ fontSize: '24px', color: isActive ? 'white' : 'gray' }} />
              )}
            </div>
            {isMobileMenu && <span className="menu-title">Firmalar</span>}
          </div>
        )}
      </NavLink>

      {/* Teklifler */}
      <NavLink
        to="/proposals"
        className="menu-link"
        onClick={handleNavigate}
      >
        {({ isActive }) => (
          <div className="menu-item-wrapper">
            <div className={`menu-item ${isActive ? 'active' : ''}`}>
              {!isMobileMenu ? (
                <Tooltip title="Teklifler" placement="right">
                  <img src={isActive ? PaperIconWhite : PaperIcon} className="icon" alt="Teklifler" />
                </Tooltip>
              ) : (
                <img src={isActive ? PaperIconWhite : PaperIcon} className="icon" alt="Teklifler" />
              )}
            </div>
            {isMobileMenu && <span className="menu-title">Teklifler</span>}
          </div>
        )}
      </NavLink>

      {/* {isAdmin && (
        <NavLink
          to="/mailing"
          className="menu-link"
          onClick={handleNavigate}
        >
          {({ isActive }) => (
            <div className="menu-item-wrapper">
              <div className={`menu-item ${isActive ? 'active' : ''}`}>
                {!isMobileMenu ? (
                  <Tooltip title="Mailing" placement="right">
                    <MessageOutlined style={{ fontSize: '24px', color: isActive ? 'white' : 'gray' }} />
                  </Tooltip>
                ) : (
                  <MessageOutlined style={{ fontSize: '24px', color: isActive ? 'white' : 'gray' }} />
                )}
              </div>
              {isMobileMenu && <span className="menu-title">Mailing</span>}
            </div>
          )}
        </NavLink>
      )} */}

      {/* İş Takip */}



      <NavLink
        to="/project-followup"
        className="menu-link"
        onClick={handleNavigate}
      >
        {({ isActive }) => (
          <div className="menu-item-wrapper">
            <div className={`menu-item ${isActive ? 'active' : ''}`}>
              {!isMobileMenu ? (
                <Tooltip title="İş Takip" placement="right">
                  <CheckListIcon style={{ fontSize: '24px', color: isActive ? 'white' : 'gray' }} />
                </Tooltip>
              ) : (
                <CheckListIcon style={{ fontSize: '24px', color: isActive ? 'white' : 'gray' }} />
              )}
            </div>
            {isMobileMenu && <span className="menu-title">İş Takip</span>}
          </div>
        )}
      </NavLink>

      {roles && !roles.some((role: any) => role.name !== 'ADMIN') && (
        <NavLink
          to="/payments"
          className="menu-link"
          onClick={handleNavigate}
        >
          {({ isActive }) => (
            <div className="menu-item-wrapper">
              <div className={`menu-item ${isActive ? 'active' : ''}`}>
                {!isMobileMenu ? (
                  <Tooltip title="Ödemeler" placement="right">
                    {isActive ? (
                      <WalletDone02Icon style={{ fontSize: '24px', color: isActive ? 'white' : 'gray' }} />
                      // <FileAddOutlined style={{ fontSize: '24px', color: isActive ? 'white' : 'gray' }} />
                    ) : (
                      <WalletDone02Icon style={{ fontSize: '24px', color: isActive ? 'white' : 'gray' }} />
                    )}
                  </Tooltip>
                ) : (
                  <WalletDone02Icon style={{ fontSize: '24px', color: isActive ? 'white' : 'gray' }} />
                )}
              </div>
              {isMobileMenu && <span className="menu-title">Ödemeler</span>}
            </div>
          )}
        </NavLink>
      )}

      {/* Cüzdan (sadece CUSTOMER olmayanlar için) */}
      {roles && !roles.some((role: any) => role.name !== 'CUSTOMER') && (
        <NavLink
          to="/wallet"
          className="menu-link"
          onClick={handleNavigate}
        >
          {({ isActive }) => (
            <div className="menu-item-wrapper">
              <div className={`menu-item ${isActive ? 'active' : ''}`}>
                {!isMobileMenu ? (
                  <Tooltip title="Cüzdan" placement="right">
                    <img src={isActive ? ValletIconWhite : ValletIcon} className="icon" alt="Cüzdan" />
                  </Tooltip>
                ) : (
                  <img src={isActive ? ValletIconWhite : ValletIcon} className="icon" alt="Cüzdan" />
                )}
              </div>
              {isMobileMenu && <span className="menu-title">Cüzdan</span>}
            </div>
          )}
        </NavLink>
      )}

      <NavLink
        to="/opportunity"
        className="menu-link"
        onClick={handleNavigate}
      >
        {({ isActive }) => (
          <div className="menu-item-wrapper">
            <div className={`menu-item ${isActive ? 'active' : ''}`}>
              {!isMobileMenu ? (
                <Tooltip title="Ajanda" placement="right">
                  {isActive ? (
                    <Calendar03Icon style={{ fontSize: '24px', color: isActive ? 'white' : 'gray' }} />
                    // <FileAddOutlined style={{ fontSize: '24px', color: isActive ? 'white' : 'gray' }} />
                  ) : (
                    <Calendar03Icon style={{ fontSize: '24px', color: isActive ? 'white' : 'gray' }} />
                  )}
                </Tooltip>
              ) : (
                <Calendar03Icon style={{ fontSize: '24px', color: isActive ? 'white' : 'gray' }} />
              )}
            </div>
            {isMobileMenu && <span className="menu-title">Ajanda</span>}
          </div>
        )}
      </NavLink>

      {canSeeReminders && (
        <NavLink
          to="/reminders"
          className="menu-link"
          onClick={handleNavigate}
        >
          {({ isActive }) => (
            <div className="menu-item-wrapper">
              <div className={`menu-item ${isActive ? 'active' : ''}`}>
                {!isMobileMenu ? (
                  <Tooltip title="Görevler" placement="right">
                    <TaskDaily01Icon style={{ fontSize: '24px', color: isActive ? 'white' : 'gray' }} />
                  </Tooltip>
                ) : (
                  <TaskDaily01Icon style={{ fontSize: '24px', color: isActive ? 'white' : 'gray' }} />
                )}
              </div>
              {isMobileMenu && <span className="menu-title">Görevler</span>}
            </div>
          )}
        </NavLink>
      )}

      {/* Destek -> "ticket.show" iznine göre yönlendirme */}
      <NavLink
        to={permissions.includes('ticket.show') ? '/all-tickets' : '/tickets'}
        className="menu-link"
        onClick={handleNavigate}
      >
        {({ isActive }) => (
          <div className="menu-item-wrapper">
            <div className={`menu-item ${isActive ? 'active' : ''}`}>
              {!isMobileMenu ? (
                <Tooltip title="Destek" placement="right">
                  {isActive ? (
                    <MessageMultiple01Icon className="icon" style={{ color: 'white' }} />
                  ) : (
                    <MessageMultiple01Icon className="icon" />
                  )}
                </Tooltip>
              ) : (
                <MessageMultiple01Icon className="icon" style={{ color: isActive ? 'white' : undefined }} />
              )}
            </div>
            {isMobileMenu && <span className="menu-title">Destek</span>}
          </div>
        )}
      </NavLink>

      {/* Raporlar */}
      {isAdmin && permissions.includes('report.show') && (
        <NavLink
          to="/reports"
          className="menu-link"
          onClick={handleNavigate}
        >
          {({ isActive }) => (
            <div className="menu-item-wrapper">
              <div className={`menu-item ${isActive ? 'active' : ''}`}>
                {!isMobileMenu ? (
                  <Tooltip title="Raporlar" placement="right">
                    {isActive ? (
                      <AnalyticsUpIcon style={{ fontSize: '24px', color: isActive ? 'white' : 'gray' }} />
                    ) : (
                      <AnalyticsUpIcon style={{ fontSize: '24px', color: isActive ? 'white' : 'gray' }} />
                    )}
                  </Tooltip>
                ) : (
                  <AnalyticsUpIcon style={{ fontSize: '24px', color: isActive ? 'white' : 'gray' }} />
                )}
              </div>
              {isMobileMenu && <span className="menu-title">Raporlar</span>}
            </div>
          )}
        </NavLink>
      )}

    </div>
  );
  };

  // Footer menü öğeleri (Ayarlar ve Çıkış)
  const renderFooterMenuItems = (isMobileMenu = false) => (
    <div className={`py-20px ${isMobileMenu ? 'mobile-menu' : ''}`}>
      {permissions.includes('settings.show') && !isFinance && (
        <NavLink
          to="/settings/roles"
          className="menu-link"
          onClick={() => isMobileMenu && setDrawerVisible(false)}
        >
          {({ isActive }) => (
            <div className="menu-item-wrapper">
              <div className={`menu-item ${isActive ? 'active' : ''}`}>
                {!isMobileMenu ? (
                  <Tooltip title="Ayarlar" placement="right">
                    <img src={isActive ? SettingsIconWhite : SettingsIcon} className="icon" alt="Settings" />
                  </Tooltip>
                ) : (
                  <img src={isActive ? SettingsIconWhite : SettingsIcon} className="icon" alt="Settings" />
                )}
              </div>
              {isMobileMenu && <span className="menu-title">Ayarlar</span>}
            </div>
          )}
        </NavLink>
      )}

      <div
        className="menu-link"
        onClick={() => {
          logout();
          isMobileMenu && setDrawerVisible(false);
        }}
      >
        <div className="menu-item-wrapper">
          <div className="menu-item">
            <Tooltip title="Çıkış" placement="right">
              <img src={ExitIcon} className="icon" alt="Exit" />
            </Tooltip>
          </div>
          {isMobileMenu && <span className="menu-title">Çıkış</span>}
        </div>
      </div>
    </div>
  );

  return (
    <>
      {loaded ? (
        <Layout className="height-100vh">
          {/* Desktop Sidebar */}
          {!isMobile && (
            <Sider width={80} className="layout-sider">
              <div className="py-20px">
                <Link to="/" className="d-flex justify-content-center mb-20px">
                  <img src={Logo} alt="logo" width={40} className="mt-16px" />
                </Link>
              </div>
              {renderMenuItems()}
              {renderFooterMenuItems()}
            </Sider>
          )}

          <Layout>
            <Content className="layout-content">
              <Header className="d-flex justify-content-between align-items-center p-20px layout-header mb-16px">
                {/* Sol taraf: Menü toggle veya başlık */}
                <div className="d-flex align-items-center">
                  {isMobile ? (
                    <div className="d-flex align-items-center">
                      <Button type="text" onClick={toggleDrawer} style={{ padding: 0 }}>
                        <MenuUnfoldOutlined style={{ fontSize: '24px', fontWeight: 'bold' }} />
                      </Button>
                      <Title level={4} style={{ margin: 0, marginLeft: 8 }}>
                        {renderPageTitle()}
                      </Title>
                    </div>
                  ) : (
                    <Title level={3} style={{ margin: 0 }}>
                      {renderPageTitle()}
                    </Title>
                  )}
                </div>

                {/* Sağ taraf: Bildirim ve Profil */}
                <div className="d-flex align-items-center gap-16px">
                  {canSeeWeeklyPlan && (
                    <>
                      <Button
                        type='primary'
                        style={{ width: 50 }}
                        onClick={() => setWeeklyPlanDrawerOpen(true)}
                      >
                       Plan
                      </Button>
                      <WeeklyPlanDrawer
                        open={weeklyPlanDrawerOpen}
                        onClose={() => setWeeklyPlanDrawerOpen(false)}
                        weekStart={weeklyPlanWeekStart}
                        onWeekStartChange={setWeeklyPlanWeekStart}
                        inspectors={weeklyInspectors}
                      />
                    </>
                  )}
                  <Notifications />
                  <Row
                    gutter={8}
                    align={'middle'}
                    justify={'center'}
                    style={{
                      backgroundColor: '#fff',
                      borderRadius: 24,
                      lineHeight: 4,
                      cursor: 'pointer',
                      minWidth: 80,
                      height: 48,
                    }}
                    onClick={() => navigate('/profile')}
                  >
                    <Col>
                      <div style={{ height: 30, display: 'flex', alignItems: 'center', paddingRight: (!isCustomer ? 5 : 0) }}>
                        <Avatar
                          size={40}
                          src={profilePhoto ?? ''}
                          style={{ border: '1px solid #d9d9d9', cursor: 'pointer' }}
                        >
                          {firstName[0]?.toUpperCase()}
                        </Avatar>
                        <span className="hide-on-mobile" style={{ marginLeft: 5 }}>
                          {firstName ?? ''} {lastName ?? ''}
                        </span>
                      </div>
                    </Col>
                    {isCustomer && <Col>
                      <img
                        src={profileCompletion === 'true' ? ConfirmIconGreen : ConfirmIcon}
                        alt=""
                        style={{ display: 'flex', alignContent: 'center', marginRight: 10 }}
                      />
                    </Col>}
                  </Row>
                </div>
              </Header>

              <div className="layout-content-container" style={{ height: 'calc(100vh - 150px)', overflowY: 'auto' }}>
                <Outlet />
               
              </div>
               <div className="layout-content-footer">
                  KONTROLX 2025 © Tüm hakları saklıdır · Versiyon {appVersion}
                </div>
            </Content>
          </Layout>

          {/* Mobil Menü Drawer */}
          {isMobile && (
            <Drawer title="Menü" placement="left" onClose={toggleDrawer} open={drawerVisible}>
              {renderMenuItems(true)}
              {renderFooterMenuItems(true)}
            </Drawer>
          )}
        </Layout>
      ) : null}
    </>
  );
};

export default MainLayout;
